import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Order } from '../../orders/models/order.models';
import { CancellationReasonModalComponent } from '../cancellation-reason-modal/cancellation-reason-modal.component';

/**
 * Modal component for initiating and confirming the cancellation of an Order, including collecting a cancellation reason when required.
 * @component
 * @selector app-cancel-order-modal
 * @example
 *   <app-cancel-order-modal
 *     [order]="selectedOrder"
 *     [isOpen]="isCancelModalOpen"
 *     (close)="handleClose()"
 *     (confirm)="handleConfirm($event)">
 *   </app-cancel-order-modal>
 * @input {Order | null} order - The Order object to be cancelled. If null, the modal has no target order.
 * @input {boolean} isOpen - Controls whether the cancel modal is visible.
 * @output {EventEmitter<void>} close - Emitted when the modal is requested to be closed without confirmation.
 * @output {EventEmitter<{ order: Order; reason: string }>} confirm - Emitted after a cancellation reason is provided and cancellation is confirmed; payload includes the order and the provided reason.
 *
 * Key properties:
 * - showCancellationReasonModal {boolean}: Internal flag that controls visibility of the nested cancellation-reason modal.
 *
 * Key methods:
 * - onClose(): void
 *   Emits the close event to signal the parent to close the modal.
 *
 * - onConfirm(): void
 *   Initiates the confirm flow. If an order is present, opens the cancellation reason modal to collect a reason before finalizing.
 *
 * - onCancel(): void
 *   Alias to onClose; closes the modal without confirming cancellation.
 *
 * - onCancellationReasonConfirm(data: { item: Order | any; reason: string }): void
 *   Handles confirmation from the nested cancellation reason modal. Hides the nested modal and emits the confirm event with the order and reason.
 *
 * - onCancellationReasonClose(): void
 *   Hides the nested cancellation reason modal without emitting a final confirm.
 *
 * Lifecycle hooks:
 * - This component does not implement any special Angular lifecycle hooks (e.g., ngOnInit) and relies on input bindings and events to manage its state.
 */
@Component({
  selector: 'app-cancel-order-modal',
  standalone: true,
  imports: [CommonModule, CancellationReasonModalComponent],
  templateUrl: './cancel-order-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./cancel-order-modal.component.scss'],
})
export class CancelOrderModalComponent {
  @Input() order: Order | null = null;
  @Input() isOpen = false;
  @Output() close = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<{ order: Order; reason: string }>();

  showCancellationReasonModal = false;

  onClose(): void {
    this.close.emit();
  }

  onConfirm(): void {
    if (this.order) {
      this.showCancellationReasonModal = true;
    }
  }

  onCancel(): void {
    this.close.emit();
  }

  onCancellationReasonConfirm(data: { item: Order | any; reason: string }): void {
    this.showCancellationReasonModal = false;
    this.confirm.emit({ order: data.item, reason: data.reason });
  }

  onCancellationReasonClose(): void {
    this.showCancellationReasonModal = false;
  }
}
